import {Progress} from "@/components/ui/progress";
import {Skeleton} from "@/components/ui/skeleton";
import {PAGE_ROUTES} from "@/constants/Routes";
import {RootState} from "@/dataStore/store";
import {setPlatformBearer, setUser} from "@/dataStore/userStore";
import {useGetCurrentUserQuery} from "@/services/adminApi";
import {Menu, X} from "lucide-react";
import {ErrorBoundary} from "@/components/ui/ErrorBoundary";
import {lazy, Suspense, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Outlet, useNavigate} from "react-router-dom";

const LazyNavbar = lazy(() => import("../components/navbar/navbar"));

const isTokenExpired = () => {
  const token = localStorage.getItem("access_token");

  if (!token) return true;

  try {
    // Get payload from token
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(window.atob(base64));

    // Check if token has expiration claim
    if (!payload.exp) {
      throw new Error("Token has no expiration claim");
    }

    // Compare expiration timestamp with current time
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch (error) {
    console.error("Error checking token expiration:", error);
    return true;
  }
};

export const Layout = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const nav = useNavigate();
  const dispatch = useDispatch();

  const platformBearer = useSelector(
    (state: RootState) => state.userState.platformBearer
  );

  const activeOrgId = useSelector(
    (state: RootState) => state.organizationState.data.id
  );

  const {
    data: currentUser,
    isFetching,
    isSuccess,
  } = useGetCurrentUserQuery(undefined, {
    skip: !platformBearer,
  });

  useEffect(() => {
    if (platformBearer && platformBearer?.length > 0) {
      setLoading(false);
    } else if (!isTokenExpired()) {
      const token = localStorage.getItem("access_token");
      dispatch(setPlatformBearer(token));
      setLoading(false);
    } else {
      // user has not logged in yet
      nav(PAGE_ROUTES.LOGIN);
    }
  }, [dispatch, nav, platformBearer]);

  useEffect(() => {
    if (isSuccess && currentUser) {
      dispatch(setUser(currentUser));
    }
  }, [currentUser, dispatch, isSuccess, nav]);

  return !(loading || isFetching) ? (
    <div className="h-[100vh] flex relative overflow-hidden">
      {/* Animated Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-muted/20 to-background/95 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 via-transparent to-accent/10"></div>
      </div>

      {/* Subtle Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-primary/3 rounded-full blur-xl"></div>
        <div className="absolute top-3/4 right-1/4 w-48 h-48 bg-accent/3 rounded-full blur-2xl"></div>
        <div className="absolute bottom-1/4 left-1/3 w-24 h-24 bg-muted/8 rounded-full blur-lg"></div>
      </div>

      {/* Subtle Grid Pattern Overlay */}
      <div
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)`,
          backgroundSize: "40px 40px",
        }}
      ></div>

      {/* Mobile Menu Button */}
      <button
        className="fixed top-4 left-4 z-[100003] md:hidden bg-sidebar text-sidebar-foreground p-2 rounded-lg shadow-lg"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      >
        {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Main Content */}
      <div className="relative z-10 flex w-full">
        {/* Desktop & Tablet Sidebar */}
        <div className="hidden md:block">
          <Suspense fallback={<Skeleton className="w-[280px] h-[98%] m-2" />}>
            <LazyNavbar isMobile={false} isTablet={true} />
          </Suspense>
        </div>

        {/* Mobile Sidebar */}
        {isMobileMenuOpen && (
          <div className="fixed inset-y-0 left-0 z-[100002] md:hidden w-[280px] h-full">
            <Suspense fallback={<Skeleton className="w-[280px] h-full" />}>
              <LazyNavbar 
                isMobile={true} 
                onMobileClose={() => setIsMobileMenuOpen(false)} 
              />
            </Suspense>
          </div>
        )}

        {/* Mobile Overlay */}
        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-[100001] md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
            style={{ marginLeft: '280px' }}
          />
        )}

        {/* Main Content Area */}
        <div className="flex-1 min-w-0 w-full md:w-[calc(100%-280px)] flex flex-col">
          <div className="pt-14 md:pt-0 px-2 md:px-4 lg:px-6 flex-1 min-h-0 flex flex-col">
            <ErrorBoundary>
              <Outlet key={activeOrgId} />
            </ErrorBoundary>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div className="flex items-center justify-center h-[100vh] relative overflow-hidden">
      {/* Animated Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-muted/20 to-background/95 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 via-transparent to-accent/10"></div>
      </div>

      {/* Subtle Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-primary/3 rounded-full blur-xl"></div>
        <div className="absolute top-3/4 right-1/4 w-48 h-48 bg-accent/3 rounded-full blur-2xl"></div>
        <div className="absolute bottom-1/4 left-1/3 w-24 h-24 bg-muted/8 rounded-full blur-lg"></div>
      </div>

      {/* Subtle Grid Pattern Overlay */}
      <div
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)`,
          backgroundSize: "40px 40px",
        }}
      ></div>

      <Progress
        variant="continuous-circular"
        className="w-12 h-12 relative z-10"
      />
    </div>
  );
};
